import {create} from 'zustand';
import {persist, createJSONStorage} from 'zustand/middleware';

/**
 * Persisted "add to history" store — lets a super admin snapshot the exact
 * spot they're looking at (a specific user's sub-tab, and later an open
 * action/modal + applied filters) and jump back to it from the header.
 *
 * Keyed by the logged-in admin's id so each admin keeps their own history.
 *
 * Entry shape:
 *  {
 *    id: string,            // unique
 *    path: string,          // absolute route to restore (may include an id)
 *    title: string,         // primary label, e.g. the sub-tab name
 *    subtitle: string,      // secondary label, e.g. the user's name
 *    context: {             // structured data for rendering / future replay
 *      scope: 'user',
 *      userId, userName, tabKey, tabLabel,
 *    },
 *    filters: object|null,  // reserved: filters applied on the tab
 *    action: object|null,   // reserved: an action/modal to re-trigger
 *    createdAt: number,
 *  }
 */

// Hard cap: history never holds more than this; saving a 21st entry pops the
// oldest off the bottom (entries are kept newest-first).
const MAX_ENTRIES = 20;

export const useHistoryStore = create(
  persist(
    (set, get) => ({
      // { [adminId]: HistoryEntry[] } — newest first
      historyByUser: {},

      addToHistory: (adminId, entry) => {
        if (!adminId || !entry?.path) return;
        set(state => {
          const current = state.historyByUser[adminId] || [];
          // De-dupe by a stable identity so re-saving the same thing (same
          // ticket id, same action, or same URL) updates in place and floats to
          // the top — while distinct items (e.g. different ticket ids) coexist.
          const keyOf = item =>
            item.dedupeKey ||
            `${item.path}|${item.action ? JSON.stringify(item.action) : ''}`;
          const entryKey = keyOf(entry);
          const deduped = current.filter(item => keyOf(item) !== entryKey);
          const next = [entry, ...deduped].slice(0, MAX_ENTRIES);
          return {
            historyByUser: {...state.historyByUser, [adminId]: next},
          };
        });
      },

      removeFromHistory: (adminId, entryId) => {
        if (!adminId) return;
        set(state => ({
          historyByUser: {
            ...state.historyByUser,
            [adminId]: (state.historyByUser[adminId] || []).filter(
              item => item.id !== entryId,
            ),
          },
        }));
      },

      clearHistory: adminId => {
        if (!adminId) return;
        set(state => ({
          historyByUser: {...state.historyByUser, [adminId]: []},
        }));
      },

      getHistory: adminId => get().historyByUser[adminId] || [],
    }),
    {
      name: 'dashboardHistory',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
